import React from 'react';
import degreesToRadians from '../../../helpers/degreesToRadians';
/**
 * Creates a path circle Arc based on the degrees and other inputs.
 * Nothing is rendered at 360 degrees or greater. Instead, use the circle element.
 * IMPORTANT: The result of this component must be enclosed in an svg element
 * https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d#path_commands
 */
const Arc = ({ sweepAngle, startAngle = 0, rotation = 0, radius = 100, center = radius * 2, strokeWidth = radius * 2, color = 'black' }) => {
    const { cos, sin, PI } = Math;
    /**
     * Multiply the first element of the 2 matrices (a, c) by x,
     * and the second element of the 2 matrices (b, d) by y.
     * Return as a pair.
     */
    const matrixTimes = ([[a, b], [c, d]], [x, y]) => [
        a * x + b * y,
        c * x + d * y
    ];
    /** Add a1 to b1, a2 to b2, and return as a pair */
    const vectorAdd = ([a1, a2], [b1, b2]) => [
        a1 + b1,
        a2 + b2
    ];
    // Convert degrees to radians
    const sweepAngleRadians = degreesToRadians(sweepAngle) % (2 * PI);
    const startAngleRadians = degreesToRadians(startAngle);
    const rotationRadians = degreesToRadians(rotation);
    const rotationMatrix = [
        [cos(rotationRadians), -sin(rotationRadians)],
        [sin(rotationRadians), cos(rotationRadians)]
    ];
    /** Starting x and y */
    const [startX, startY] = vectorAdd(matrixTimes(rotationMatrix, [
        radius * cos(startAngleRadians),
        radius * sin(startAngleRadians)
    ]), [center, center]);
    /** Rotation (or x and y) */
    const [rotationX, rotationY] = vectorAdd(matrixTimes(rotationMatrix, [
        radius * cos(startAngleRadians + sweepAngleRadians),
        radius * sin(startAngleRadians + sweepAngleRadians)
    ]), [center, center]);
    /** Large arc flag (large arc (1) or small arc (0)) */
    const largeArcFlag = sweepAngleRadians > PI ? 1 : 0;
    /** Sweep flag (direction of turn, clockwise (1) or counter-clockwise (0)) */
    const sweepFlag = sweepAngleRadians > 0 ? 1 : 0;
    /** Angle of rotation */
    const rotationAngle = (rotationRadians / 2) * PI * 360;
    /** Final result - d attribute of a path element */
    const svgDAttribute = `M ${startX} ${startY} A ${radius} ${radius} ${rotationAngle} ${largeArcFlag} ${sweepFlag} ${rotationX} ${rotationY}`;
    return (React.createElement("path", { d: svgDAttribute, stroke: color, strokeWidth: strokeWidth, fill: "none" }));
};
export default Arc;
